import { useMemo } from 'react';

import { cn } from '@crossnokaye/ux-common';

import {
  ButtonComponentActionConfigVariant,
  TableColumnConfig,
  TableRowConfig,
} from '@repo/sdk/models/components';

import { DropdownCell, type DropdownCellOption } from '@/components/data-table';
import { mapLegacyButtonVariantToClassName } from '@/features/equipment-browser/utils';
import { Input, InputField, InputUnit } from '@/ux-common/components/input/input';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';

import {
  getColumnInputType,
  getColumnUnitInfo,
  getCommonDisplayValue,
  getOptionsForColumn,
} from '../equipment-bulk-edit.helpers';

interface EquipmentBulkEditSettingProps {
  column: TableColumnConfig;
  onSettingFocus?: (columnId: string) => void;
  onSettingBlur?: () => void;
  onValueChange: (columnId: string, value: string) => void;
  rows: TableRowConfig[];
  selectedRowIds: string[];
  value: string | undefined;
}

/**
 * Renders a single column's bulk-edit control (dropdown or text input)
 * with dirty-state visual feedback (yellow text) when a value has been set.
 * Converts from source units to display units for display, and converts
 * back from display units to source units on change.
 */
export const EquipmentBulkEditSetting = ({
  column,
  onSettingFocus,
  onSettingBlur,
  onValueChange,
  rows,
  selectedRowIds,
  value,
}: EquipmentBulkEditSettingProps) => {
  const inputType = getColumnInputType(column.columnId, rows, selectedRowIds);
  const unitsLabel = getColumnUnitInfo(column.columnId, rows, selectedRowIds)?.unitsLabel;

  const commonDisplayValue = useMemo(
    () => getCommonDisplayValue(column.columnId, inputType, rows, selectedRowIds),
    [column.columnId, inputType, rows, selectedRowIds]
  );

  // Adapt SDK options to the cell's option shape (value + label only).
  const dropdownOptions = useMemo<DropdownCellOption[]>(
    () =>
      getOptionsForColumn(column.columnId, rows, selectedRowIds).map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [column.columnId, rows, selectedRowIds]
  );

  // Don't render readonly columns
  if (inputType === 'readonly') return null;

  const isDirty = !!value && value !== commonDisplayValue;

  return (
    <Stack key={column.columnId} className="stack-xs">
      {/* Stays a paragraph, and stays outside the group, until the dropdown branch
          below can take a registry label too (UXD-21). Moving only the input branch
          to `InputLabel` would put two label sizes side by side in one panel. */}
      <Typography as="p" className="text-body-base text-ckds-text-fg-tier3">
        {column.label ?? column.columnId}
      </Typography>
      {inputType === 'dropdown' ? (
        <div
          onFocus={() => onSettingFocus?.(column.columnId)}
          onBlur={(event) => {
            const nextFocusedElement = event.relatedTarget;
            if (
              nextFocusedElement instanceof Node &&
              event.currentTarget.contains(nextFocusedElement)
            ) {
              return;
            }
            onSettingBlur?.();
          }}
        >
          <DropdownCell
            className={cn(
              mapLegacyButtonVariantToClassName(ButtonComponentActionConfigVariant.Tier3),
              isDirty && 'text-ckds-base-caution',
              'w-full'
            )}
            onSelect={(option) => onValueChange(column.columnId, option.value)}
            options={dropdownOptions}
            placeholder="Select value to change to"
            value={value}
          />
        </div>
      ) : (
        <Input className="w-full" state={isDirty ? 'dirty' : undefined}>
          <InputField
            aria-label={column.label ?? column.columnId}
            // The state class owns the border; the value colour is ours and has to go
            // on the control — `className` lands on the bordered shell and would
            // colour nothing.
            controlClassName={cn(isDirty && 'text-ckds-base-caution')}
            placeholder="Enter value to change to"
            value={value}
            onBlur={() => onSettingBlur?.()}
            onChange={(e) => onValueChange(column.columnId, e.target.value)}
            onFocus={() => onSettingFocus?.(column.columnId)}
          >
            {unitsLabel && <InputUnit>{unitsLabel}</InputUnit>}
          </InputField>
        </Input>
      )}
    </Stack>
  );
};
